import { useCallback, useEffect, useMemo, useRef } from "react";

const isEditableTarget = (target) => {
  if (!target || typeof target !== "object") return false;
  const el = target;
  const tag = (el.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.isContentEditable) return true;
  return Boolean(el.closest?.("[contenteditable='true'], .ql-editor, .ProseMirror"));
};

const isTypingContext = () => isEditableTarget(document.activeElement);

/** ↑ = anterior na lista, ↓ = próxima (fora de campos de digitação). */
const isQueueNavPreviousKey = (event) =>
  !event.ctrlKey &&
  !event.metaKey &&
  !event.altKey &&
  !event.shiftKey &&
  event.key === "ArrowUp";

const isQueueNavNextKey = (event) =>
  !event.ctrlKey &&
  !event.metaKey &&
  !event.altKey &&
  !event.shiftKey &&
  event.key === "ArrowDown";

/**
 * Navegação anterior/próxima dentro de uma fila de conversas (lista filtrada do chat).
 */
export default function useConversationQueueNavigation({
  currentConversationId,
  conversations = [],
  totalCount = null,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  onNavigate,
  enabled = true,
}) {
  const pendingDirectionRef = useRef(null);

  const currentIndex = useMemo(() => {
    if (currentConversationId == null) return -1;
    return conversations.findIndex(
      (c) => String(c.id) === String(currentConversationId)
    );
  }, [conversations, currentConversationId]);

  const position = currentIndex >= 0 ? currentIndex + 1 : null;
  const total =
    typeof totalCount === "number" && totalCount > 0
      ? totalCount
      : conversations.length > 0
        ? conversations.length
        : null;

  const previousConversation =
    currentIndex > 0 ? conversations[currentIndex - 1] : null;
  const nextConversation =
    currentIndex >= 0 && currentIndex < conversations.length - 1
      ? conversations[currentIndex + 1]
      : null;

  const canGoPrevious = Boolean(previousConversation);
  const canGoNext = Boolean(nextConversation) || Boolean(hasNextPage);
  const inQueue = currentIndex >= 0;

  const navigateTo = useCallback(
    (conversation) => {
      if (!conversation?.id || typeof onNavigate !== "function") return;
      onNavigate(conversation);
    },
    [onNavigate]
  );

  const goPrevious = useCallback(() => {
    if (previousConversation) navigateTo(previousConversation);
  }, [navigateTo, previousConversation]);

  const goNext = useCallback(async () => {
    if (nextConversation) {
      navigateTo(nextConversation);
      return;
    }
    if (
      currentIndex >= 0 &&
      currentIndex === conversations.length - 1 &&
      hasNextPage &&
      typeof fetchNextPage === "function" &&
      !isFetchingNextPage
    ) {
      pendingDirectionRef.current = "next";
      await fetchNextPage();
    }
  }, [
    nextConversation,
    navigateTo,
    currentIndex,
    conversations.length,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  ]);

  useEffect(() => {
    if (pendingDirectionRef.current !== "next") return;
    if (currentIndex < 0) {
      pendingDirectionRef.current = null;
      return;
    }
    const candidate = conversations[currentIndex + 1];
    if (candidate) {
      pendingDirectionRef.current = null;
      navigateTo(candidate);
      return;
    }
    if (!hasNextPage && !isFetchingNextPage) {
      pendingDirectionRef.current = null;
    }
  }, [
    conversations,
    currentIndex,
    hasNextPage,
    isFetchingNextPage,
    navigateTo,
  ]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event) => {
      if (event.repeat || isTypingContext()) return;

      const isPrev = isQueueNavPreviousKey(event);
      const isNext = isQueueNavNextKey(event);
      if (!isPrev && !isNext) return;

      event.preventDefault();
      event.stopPropagation();

      if (isPrev && canGoPrevious) {
        goPrevious();
      } else if (isNext && canGoNext) {
        void goNext();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enabled, canGoPrevious, canGoNext, goPrevious, goNext]);

  return {
    position,
    total,
    currentIndex,
    inQueue,
    canGoPrevious,
    canGoNext,
    isFetchingNextPage,
    goPrevious,
    goNext,
  };
}
